import { getServerBaseUrl } from '@/utils/serverUrl'

const CAPABILITIES_PATH = '/api/admin/capabilities'
const MAX_RETRIES = 3
const BASE_DELAY_MS = 400

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export type AdminCapabilitiesResult =
  | {
      ok: true
      meetsGlobalAdminRouteGate: boolean
      meetsAdminAccessRouteGate: boolean
    }
  | { ok: false; transientFailure: boolean }

/**
 * Fetches server-attested elevated-admin flags (same rules as requireGlobalAdmin / requireAdminAccess).
 * Retries on 5xx/503/network errors; 401 is definitive (treat as not elevated).
 */
export async function fetchAdminCapabilitiesWithRetry(
  sessionToken: string
): Promise<AdminCapabilitiesResult> {
  const url = `${getServerBaseUrl()}${CAPABILITIES_PATH}`

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
      })

      if (response.status === 503 || response.status >= 500) {
        await sleep(BASE_DELAY_MS * 2 ** attempt)
        continue
      }

      if (response.status === 401) {
        return {
          ok: true,
          meetsGlobalAdminRouteGate: false,
          meetsAdminAccessRouteGate: false,
        }
      }

      if (!response.ok) {
        await sleep(BASE_DELAY_MS * 2 ** attempt)
        continue
      }

      const body = (await response.json()) as {
        success?: boolean
        meetsGlobalAdminRouteGate?: boolean
        meetsAdminAccessRouteGate?: boolean
      }

      if (!body || body.success !== true) {
        await sleep(BASE_DELAY_MS * 2 ** attempt)
        continue
      }

      return {
        ok: true,
        meetsGlobalAdminRouteGate: !!body.meetsGlobalAdminRouteGate,
        meetsAdminAccessRouteGate: !!body.meetsAdminAccessRouteGate,
      }
    } catch {
      await sleep(BASE_DELAY_MS * 2 ** attempt)
    }
  }

  return { ok: false, transientFailure: true }
}
